import React from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useReportByAuditId } from '../../survey-report/hooks/useSurveyReport';

interface ViewReportButtonProps {
  auditId: string;
  auditStatus: string;
  surveyTitle?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

export const ViewReportButton: React.FC<ViewReportButtonProps> = ({
  auditId,
  auditStatus,
  surveyTitle,
  size = 'medium',
  variant = 'outlined',
}) => {
  const navigate = useNavigate();

  const {
    data: reportResponse,
    isLoading,
    error,
  } = useReportByAuditId(auditId, {
    enabled: auditStatus === 'PUBLISHED',
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const report = reportResponse?.data;

  const handleViewReport = () => {
    if (report?.id) {
      navigate(`/app/reports/${report.id}/view`);
    }
  };

  // Don't show button if audit is not published
  if (auditStatus !== 'PUBLISHED') {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <Button variant={variant} size={size} disabled startIcon={<CircularProgress size={16} />}>
        Loading Report...
      </Button>
    );
  }

  // Show error state
  if (error) {
    return (
      <Tooltip title="Unable to load report. It may still be generating.">
        <Button variant={variant} size={size} disabled startIcon={<ViewIcon />}>
          Report Unavailable
        </Button>
      </Tooltip>
    );
  }

  // Report not found - may still be generating
  if (!report) {
    return (
      <Tooltip title="Report is being generated. Please try again in a moment.">
        <Button variant={variant} size={size} disabled startIcon={<ViewIcon />}>
          Report Generating...
        </Button>
      </Tooltip>
    );
  }

  // Report is available - show view button
  return (
    <Tooltip title={`View report: ${report.title || surveyTitle || 'Survey Report'}`}>
      <Button variant={variant} size={size} startIcon={<ViewIcon />} onClick={handleViewReport}>
        View Report
      </Button>
    </Tooltip>
  );
};
